import { useSearchParams } from 'react-router-dom';
import { useMemo, useState, useEffect } from 'react';

import { Tab, Card, Tabs, alpha, Typography } from '@mui/material';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useTranslate } from 'src/locales';
import { ListEntityType } from 'src/services/lists/lists.types';

import ListsTableView from 'src/sections/lists/lists-table-view';
import CandidatesList from 'src/sections/candidates/table/candidates-list';
import PlacementsTable from 'src/sections/candidates/placements/placements-table';
import CandidateSearchView from 'src/sections/candidates/view/candidate-search-view';

import { TenantType } from 'src/types/enums';

import InvitesTable from '../invites/table/invites-table';

enum TabValue {
  CANDIDATES = 'candidates',
  PLACEMENTS = 'placements',
  INVITES = 'invites',
  LISTS = 'lists',
  ADVANCED_SEARCH = 'advanced_search',
}

export default function CandidatesView() {

  const { t } = useTranslate();
  const tenantType = useOrgTenant();

  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedTab, setSelectedTab] = useState<TabValue>();

  const tableTabOptions = useMemo(() => {
    switch (tenantType) {
      case TenantType.Client:
        return [TabValue.CANDIDATES, TabValue.PLACEMENTS, TabValue.LISTS, TabValue.ADVANCED_SEARCH];
      case TenantType.Recruiter:
        return [TabValue.CANDIDATES, TabValue.PLACEMENTS, TabValue.INVITES, TabValue.LISTS, TabValue.ADVANCED_SEARCH];
      default:
        return [];
    }
  }, [tenantType]);

  // Advanced Search needs a proper label (its enum value has an underscore); others capitalize fine.
  const labelFor = (tab: TabValue) =>
    tab === TabValue.ADVANCED_SEARCH ? t('candidates.advanced_search', { defaultValue: 'Advanced Search' }) : tab;

  // Set default tab
  useEffect(() => {
    // eslint-disable-next-line no-useless-return
    if (selectedTab) return;

    let defaultTab: TabValue = TabValue.CANDIDATES

    const existingTab = searchParams.get('tab');
    if (existingTab && Object.values(TabValue).includes(existingTab as TabValue)) {
      // Check if the existing tab is in the available options
      if (tableTabOptions.includes(existingTab as TabValue)) {
        defaultTab = existingTab as TabValue;
      }
    }

    setSelectedTab(defaultTab);

  }, [selectedTab, searchParams, setSearchParams, tableTabOptions]);

  useEffect(() => {
    if (!selectedTab) return;

    // Only write the tab param when it's genuinely out of sync, and MERGE it into the existing query
    // rather than replacing the whole string — otherwise it wipes co-owned params such as the record
    // panel's ?preview (JT1-1529), which closes the overlay. The merge form also survives a tab-less
    // deep-link (?preview=…&preview_mode=overlay with no tab), where the guard below can't short-circuit.
    if (searchParams.get('tab') === selectedTab) return;

    const next = new URLSearchParams(searchParams);
    next.set('tab', selectedTab);
    setSearchParams(next, { replace: true });

  }, [selectedTab, searchParams, setSearchParams]);

  return (
    <Card>
      <Tabs
        value={selectedTab}
        onChange={(_event, tab) => setSelectedTab(tab)}
        sx={{
          px: 2.5,
          boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
        }}
      >
        {tableTabOptions.map((tab) => (
          <Tab
            key={tab}
            iconPosition="end"
            value={tab}
            label={
              <Typography
                variant="subtitle2"
                sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
              >
                {labelFor(tab)}
              </Typography>
            }
          />
        ))}
      </Tabs>

      {
        selectedTab === TabValue.CANDIDATES &&
        <CandidatesList />
      }

      {
        selectedTab === TabValue.PLACEMENTS &&
        <PlacementsTable />
      }

      {
        selectedTab === TabValue.INVITES &&
        <InvitesTable />
      }

      {
        selectedTab === TabValue.LISTS &&
        <ListsTableView
          entityType={ListEntityType.CANDIDATE}
        />
      }

      {
        selectedTab === TabValue.ADVANCED_SEARCH &&
        <CandidateSearchView />
      }

    </Card>
  );
}
